/** biome-ignore-all lint/correctness/noNestedComponentDefinitions: Composants personnalises du select definis localement. */

import { Box, Button, Text } from "@chakra-ui/react";
import { components, Select } from "chakra-react-select";
import { ReactElement, useCallback } from "react";

import { useIsTouchDevice } from "../../hooks";
import { PlusIcon } from "../Icons/Icons";
import { SearchSelectProperties } from "./SearchSelect";

export const MultiSearchSelect = <T,>({
  alwaysCreatable = false,
  creatableFunction,
  getOptionLabel,
  highlightResults = false,
  isClearable = true,
  options,
  placeholder,
  renderOption: RenderOption,
  setValue,
  singleValue: SingleValue,
  ...rest
}: SearchSelectProperties<T, true>): ReactElement => {
  const isTouch = useIsTouchDevice();

  const createAndSelect = useCallback(
    async (input: string) => {
      const created = await creatableFunction?.(input);
      if (created) {
        setValue?.(created);
      }
    },
    [creatableFunction, setValue],
  );

  return (
    <Select<T, true>
      chakraStyles={{
        dropdownIndicator: (provided) => ({
          ...provided,
          bg: "transparent",
          cursor: "pointer",
          px: 2,
        }),
        container: (provided) => ({
          ...provided,
          width: "100%",
        }),
        group: (provided) => ({
          ...provided,
          pb: 4,
        }),
        groupHeading: (provided) => ({
          ...provided,
          color: "gray.500",
          fontSize: "md",
        }),
        indicatorSeparator: (provided) => ({
          ...provided,
          display: "none",
        }),
        menu: (provided) => ({
          ...provided,
          shadow: "lg",
          zIndex: "10",
        }),
        menuList: (provided) => ({
          ...provided,
          minWidth: "none",

          ...(highlightResults ? { border: "2px", borderColor: "blue.500" } : {}),
        }),
        noOptionsMessage: (provided) => ({
          ...provided,
          p: 0,
          px: 2,
        }),
        placeholder: (provided) => ({
          ...provided,
          color: "gray.500",
        }),
      }}
      components={{
        ...(SingleValue
          ? {
              Input: (properties) => (
                <Box position="absolute">
                  <components.Input {...properties} name={rest.name} />
                </Box>
              ),
              SingleValue: (properties) => <SingleValue {...properties} />,
            }
          : {}),
        ...(RenderOption
          ? {
              Option: (properties) => {
                const isLast = JSON.stringify(properties.options.at(-1)) === JSON.stringify(properties.data);
                return (
                  <>
                    <Box
                      _hover={{ backgroundColor: "gray.100" }}
                      background={properties.isSelected ? "blue.500" : undefined}
                      color={properties.isSelected ? "white" : undefined}
                      cursor="pointer"
                      onClick={() => properties.selectOption(properties.data)}
                      px="3"
                      py={isTouch ? "4" : "2"}
                      w="full"
                    >
                      {RenderOption ? <RenderOption {...properties} /> : properties.children}
                    </Box>
                    {isLast && creatableFunction && alwaysCreatable && (
                      <Box pt={isTouch ? "4" : "2"} px="3">
                        <Button
                          colorScheme="info"
                          leftIcon={<PlusIcon />}
                          onClick={() => createAndSelect("")}
                          width="full"
                        >
                          Ajouter
                        </Button>
                      </Box>
                    )}
                  </>
                );
              },
            }
          : {}),
      }}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isMulti
      isOptionSelected={(option, currentValue) => {
        if (!getOptionLabel) {
          return (
            currentValue[0] &&
            (option as unknown as { label: string }).label === (currentValue[0] as unknown as { label: string }).label
          );
        }
        return currentValue[0] && getOptionLabel?.(option) === getOptionLabel?.(currentValue[0]);
      }}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      noOptionsMessage={({ inputValue }) => {
        if (creatableFunction && inputValue.trim()) {
          return (
            <Button colorScheme="info" leftIcon={<PlusIcon />} onClick={() => createAndSelect(inputValue)} width="full">
              Ajouter {inputValue}
            </Button>
          );
        }
        return <Text>Aucun résultat</Text>;
      }}
      options={options}
      placeholder={placeholder}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) as any }}
      {...rest}
    />
  );
};
